import { ThemeProvider, createTheme } from "@mui/material"
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { BsCheckSquare, BsSquare } from "react-icons/bs"
import { createUseStyles, useTheme } from "react-jss"
import { useAppDispatch, useAppSelector } from "../../../util/hooks"
import {
  dateChange,
  timeChange,
  timeZoneChange,
  toggleRunNow,
  toggleScheduleNow,
} from "../MonitorSlice"
import dayjs from "dayjs"
import "../../../index.css"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DATE_FORMAT, timeZoneOptions } from "../../../util/StaticData"
import SingleSelection from "../../../components/Filters/SingleSelection"
import { theme as baseTheme } from "../../../util/CustomTheme"
import { isValid } from "date-fns"

const useStyles = createUseStyles({
  label: {
    color: "var(--dark-neutrals-dark, #333)",
    fontFamily: ({ theme }: any) => theme.primaryFontFamily,
    fontSize: "13px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "150%", // You can keep this as a string or convert it to '1.5' for 150%
    marginRight: 10,
    minWidth: 120,
    maxWidth: (props: any) => {
      return props["maxWidth"]
    },
  },
  container: {
    height: 210,
    display: "grid",
    gridTemplateColumns: "32px 1fr",
    alignContent: "space-evenly",
  },
  runLabel: {},
  date: {},
  time: {},
  timezone: {},
  runContainer: {
    display: "flex",
    flexDirection: "column",
    color: "#000",
    fontSize: "14.571px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "normal",
  },
  runInnerWrapper: {
    display: "flex",
    gap: 10,
  },
  divider: {
    border: "1px solid black",
    height: 20,
    width: 1,
  },
  commonFilterContainer: {
    display: "flex",
    alignItems: "center",
    minHeight: 40,
    gap: 30,
  },
  scheduleContainer: {
    display: "flex",
    gap: 10,
    flexDirection: "column",
  },
  marginTop: {
    marginTop: 10,
  },
})
export const themeMui = createTheme({
  ...baseTheme,
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: 14,
          height: 31,
        },
      },
    },
  },
})

const ScheduleTheRun = () => {
  const theme: any = useTheme()
  const classes = useStyles({ theme })
  const dispatch = useAppDispatch()

  // Handle time selection
  const handleChangeTime = (newTime: any) => {
    if (isValid(newTime)) {
      dispatch(timeChange(newTime.toISOString()))
    } else {
      dispatch(timeChange(null))
    }
  }

  // Handle date selection
  const handleChangeDate = (newDate: any) => {
    dispatch(dateChange(newDate.toISOString()))
  }

  // Selectors to retrieve state values
  const runNow = useAppSelector((state) => state.monitor.newPipeLine.runNow)
  const timeZone = useAppSelector(
    (state) => state.monitor.newPipeLine.schedule.timeZone,
  )
  const date = useAppSelector((state) =>
    dayjs(state.monitor.newPipeLine.schedule.date),
  )
  const scheduleNow = useAppSelector(
    (state) => state.monitor.newPipeLine.scheduleNow,
  )

  const timezoneFilterOptions = timeZoneOptions

  // Handle toggle for running now
  const handleRunChange = (value: boolean) => {
    dispatch(toggleRunNow(value))
    dispatch(toggleScheduleNow(!value))
  }

  // Handle toggle for scheduling
  const handleScheduleChange = (value: boolean) => {
    dispatch(toggleScheduleNow(value))
    dispatch(toggleRunNow(!value))
  }

  return (
    <div>
      <div className={classes.container}>
        {/* Run Now Option */}
        {runNow ? (
          <BsCheckSquare
            onClick={() => handleRunChange(false)}
            fill={"#25d4cf"}
            size={16}
          />
        ) : (
          <BsSquare
            onClick={() => handleRunChange(true)}
            fill={"#25d4cf"}
            size={16}
          />
        )}
        <div className={classes.runContainer}>
          <div className={classes.label}>Run the pipeline now</div>
        </div>

        {/* Schedule Now Option */}
        {scheduleNow ? (
          <BsCheckSquare
            onClick={() => handleScheduleChange(false)}
            fill={"#25d4cf"}
            className={classes.marginTop}
            size={16}
          />
        ) : (
          <BsSquare
            onClick={() => handleScheduleChange(true)}
            fill={"#25d4cf"}
            className={classes.marginTop}
            size={16}
          />
        )}
        <div className={classes.scheduleContainer}>
          {/* Timezone Selection */}
          <div className={classes.commonFilterContainer}>
            <div className={classes.label}>Select Timezone:</div>
            <SingleSelection
              minWidth={297}
              maxWidth={297}
              changeFunc={(value: string) => dispatch(timeZoneChange(value))}
              filterValue={timeZone}
              options={timezoneFilterOptions}
              isSearchable={true}
              label="Timezone"
              minHeight={29}
              maxMenuHeight={120}
              isDisabled={runNow}
              showLabel={false}
            />
          </div>

          {/* Time Selection */}
          <div className={classes.commonFilterContainer}>
            <div className={classes.label}>Select Time:</div>
            <ThemeProvider theme={themeMui}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  label=""
                  sx={{
                    "& .MuiInputBase-input": {
                      minWidth: "233px",
                    },
                  }}
                  disabled={runNow}
                  onChange={(newValue: any) => handleChangeTime(newValue)}
                />
              </LocalizationProvider>
            </ThemeProvider>
          </div>

          {/* Date Selection */}
          <div className={classes.commonFilterContainer}>
            <div className={classes.label}>Select Date:</div>
            <ThemeProvider theme={themeMui}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label=""
                  value={date}
                  disablePast
                  disabled={runNow}
                  onChange={(newValue) => handleChangeDate(newValue)}
                  format={DATE_FORMAT.toUpperCase()}
                  sx={{
                    "& .MuiInputBase-input": {
                      minWidth: "233px",
                    },
                  }}
                />
              </LocalizationProvider>
            </ThemeProvider>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ScheduleTheRun
